// Two percentile sets • "standard" = median (P50) fixation • "conservative" = 30th percentile — a
// high-confidence FLOOR ("≥70% sure fixation is at least this"), NOT a safe-planning ceiling. To
// avoid UNDER-applying K you'd want a high percentile (P80–P90), which isn't tabulated — don't read
// the conservative column as the safe choice.
import type { ClayGroupKey } from "../types/contract";
import { BUNDLED_REFERENCE } from "./referenceData";

const kFixationDoc = BUNDLED_REFERENCE["k-fixation"];

export type KFixSet = "conservative" | "standard";

// 7 minerals + smectite fallback, mapped onto our 8 clay functional groups smectite -> the
// can't-split FALLBACK (0.03/0.08): routine mineralogy won't separate beidellite (tetrahedral
// charge, ~3× fixer, 0.07/0.15) from montmorillonite (octahedral 0.02/0.05); the fallback is
// weighted toward the montmorillonite-dominant reality.
export const K_FIXATION_COEFF_SETS: Record<
  KFixSet,
  Record<ClayGroupKey, number>
> = {
  conservative: kFixationDoc.sets.conservative,
  standard: kFixationDoc.sets.standard,
};
export let DEFAULT_KFIX_SET: KFixSet = kFixationDoc.default_set as KFixSet;
export let DEFAULT_RELEASE = kFixationDoc.default_release;
// Total clay % at/above which the mineral coefficient applies in full; below, fixation is muted
// linearly (a mineral-rich clay at 8% total clay fixes far less absolute K than at 35%).
export let CLAY_ABUNDANCE_REF_PCT = kFixationDoc.clay_abundance_ref_pct;

export interface KFixationDocument {
  default_set: KFixSet;
  default_release: number;
  clay_abundance_ref_pct: number;
  sets: Record<KFixSet, Record<ClayGroupKey, number>>;
}

export function installKFixation(doc: KFixationDocument): void {
  K_FIXATION_COEFF_SETS.conservative = doc.sets.conservative;
  K_FIXATION_COEFF_SETS.standard = doc.sets.standard;
  DEFAULT_KFIX_SET = doc.default_set;
  DEFAULT_RELEASE = doc.default_release;
  CLAY_ABUNDANCE_REF_PCT = doc.clay_abundance_ref_pct;
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

// Clay-fraction-weighted mean of the set's coefficients (Σ clay-share × coef, robust to partial
// sums). = the GROSS fixation fraction for a soil where the clay fraction is agronomically abundant.
export function mineralFixationIndex(
  clayGroups: Partial<Record<ClayGroupKey, number>>,
  set: KFixSet = DEFAULT_KFIX_SET,
): number {
  const coef = K_FIXATION_COEFF_SETS[set];
  let weighted = 0;
  let total = 0;
  for (const key of Object.keys(coef) as ClayGroupKey[]) {
    const pct = clayGroups[key];
    if (pct != null && Number.isFinite(pct) && pct > 0) {
      weighted += pct * coef[key];
      total += pct;
    }
  }
  return total > 0 ? weighted / total : 0;
}

// Linear ramp to full at the reference % unknown clay → 1 (neutral — don't understate on a missing
// value).
export function clayAbundanceFactor(
  totalClayPct: number | null | undefined,
): number {
  if (totalClayPct == null || !Number.isFinite(totalClayPct)) return 1;
  return clamp01(totalClayPct / CLAY_ABUNDANCE_REF_PCT);
}

// GROSS fraction of applied K fixed in the first season = mineral index × clay abundance (0..~0.55).
export function grossKFixation(
  clayGroups: Partial<Record<ClayGroupKey, number>>,
  totalClayPct: number | null | undefined,
  set: KFixSet = DEFAULT_KFIX_SET,
): number {
  return (
    mineralFixationIndex(clayGroups, set) * clayAbundanceFactor(totalClayPct)
  );
}

// K build-up EFFICIENCY (0<eff≤1). Fixation is GROSS; fixed K partly RELEASES in-season, so the
// NET unavailable fraction = gross × (1 − release). efficiency = 1 − net; the K₂O buildup is
// DIVIDED by it. Efficiency floored at 0.1 so the rec never blows up.
export function kBuildupEfficiency(
  gross: number,
  release: number = DEFAULT_RELEASE,
): number {
  const net = clamp01(gross) * (1 - clamp01(release));
  return 1 - Math.min(0.9, Math.max(0, net));
}
